import { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  Datepicker,
  Dropdown,
  Modal,
  Radio,
  Text,
} from '@quintoandar-tokko/jopijs';
import { format } from 'date-fns';
import styles from '../filterBar.module.scss';
import Trans from '@/components/shared/Trans';
import translations from '../FilterBar.trans.json';
import { RANGE_DATE_PRESET } from '../constants/rangeDateOptions';
import { getLang } from '@/store/slices/components/calendarEvents/utils';
import FilterSelectControl, {
  useFilterSelectionEnabled,
} from '../FilterSelectControl';

function RangeDateDropdownView({
  rangeDateOptions,
  currentSelection,
  valueTextButton,
  filled,
  dataTestId,
  className,
  buttonClassName,
  selection,
  onSelectPreset,
  onApplyCustom,
}) {
  const highlightSelection = useFilterSelectionEnabled();
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);

  const handleApplyCustomDate = () => {
    if (!fromDate) return;
    const safetyTo = toDate ?? fromDate;

    onApplyCustom({
      id: RANGE_DATE_PRESET.CUSTOM,
      text: `${fromDate.toLocaleDateString()} - ${safetyTo.toLocaleDateString()}`,
      value: {
        from: format(fromDate, 'yyyy-MM-dd'),
        to: format(safetyTo, 'yyyy-MM-dd'),
      },
    });
    setShowModal(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFromDate(new Date());
    setToDate(new Date());
  };

  const handleChangeCustomDate = (dates) => {
    setFromDate(dates[0]);
    setToDate(dates[1]);
  };

  const isCurrentDay = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const getDayClassName = (date) =>
    isCurrentDay(date) ? 'datepicker-today-highlight' : '';

  const isCustomSelected = currentSelection?.id === RANGE_DATE_PRESET.CUSTOM;
  const dropdownClassName = className || `dropdown ${styles.dropdownList}`;
  const useSelectionChrome = Boolean(selection) && highlightSelection;
  const showSelectedBorder = !useSelectionChrome && currentSelection;
  const triggerClassName = buttonClassName
    ? `${buttonClassName}${
        showSelectedBorder ? ` ${styles.rangeDateButtonSelected}` : ''
      }`
    : `${styles.dropdownButton}${
        showSelectedBorder ? ` ${styles.rangeDateButtonSelected}` : ''
      }`;
  const button = (
    <Dropdown.Button
      variant="dropdownDefault"
      className={triggerClassName}
      text={valueTextButton}
      filled={useSelectionChrome ? undefined : filled}
      data-testid={dataTestId}
    />
  );

  return (
    <>
      <Dropdown height="auto" className={dropdownClassName}>
        {useSelectionChrome ? (
          <FilterSelectControl
            filled={selection.filled}
            tooltip={selection.tooltip}
            onClear={selection.onClear}
            clearLabel={selection.clearLabel}
          >
            {button}
          </FilterSelectControl>
        ) : (
          button
        )}
        <Dropdown.Items
          clickClose
          height={400}
          className={styles.dropdownItemsRangeDate}
        >
          <Box className={styles.rangeDateOptions}>
            {rangeDateOptions.map((option) => (
              <Box
                className={styles.rangeDateItem}
                key={option.id}
                onClick={() => onSelectPreset(option)}
              >
                <Radio
                  id={option.id}
                  checked={currentSelection?.id === option.id}
                />
                <Text>{option.text}</Text>
              </Box>
            ))}

            <Box
              className={styles.rangeDateItem}
              onClick={() => setShowModal(true)}
            >
              <Radio checked={isCustomSelected} />
              <Text>{Trans(translations, 'CustomDate')}</Text>
            </Box>
          </Box>
        </Dropdown.Items>
      </Dropdown>
      {showModal &&
        createPortal(
          <Modal modalBG blockScroll className={styles.modal}>
            <Modal.Header
              closeIcon={handleCloseModal}
              variant={['start', 'l']}
              text={Trans(translations, 'CustomDate')}
            />

            <Box className={styles.dateSelectorsContainer}>
              <Box className={styles.dateSelector}>
                <Datepicker
                  selectsRange
                  inline
                  locale={getLang()}
                  onChange={handleChangeCustomDate}
                  startDate={fromDate}
                  endDate={toDate}
                  dayClassName={getDayClassName}
                  maxDate={new Date()}
                />
              </Box>

              <Box className={styles.applyButtonDateContainer}>
                <Button
                  className={styles.applyButton}
                  variant={['primary', 'small']}
                  onClick={handleApplyCustomDate}
                  disabled={!fromDate}
                >
                  {Trans(translations, 'Apply')}
                </Button>
              </Box>
            </Box>
          </Modal>,
          document.querySelector('body')
        )}
    </>
  );
}

export default RangeDateDropdownView;
